import { useState, useEffect, useRef } from "react";
import { Target } from "lucide-react";
import Hero from "../components/landing/Hero";
import LiveTicker from "../components/landing/LiveTicker";
import GamesGrid from "../components/landing/GamesGrid";
import HowItWorks from "../components/landing/HowItWorks";
import ProvablyFair from "../components/landing/ProvablyFair";
import Leaderboard from "../components/landing/Leaderboard";
import FAQ from "../components/landing/FAQ";

interface DashboardProps {
  onPlayGame: (gameId: 'pong' | 'fps' | 'tower-defense' | 'snake' | 'math', wager?: number) => void;
  onOpenTournaments: () => void;
}

const AK47Icon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 120 40" className={className} fill="currentColor">
    <rect x="8" y="17" width="70" height="6" rx="1"/>
    <rect x="10" y="14" width="45" height="3" rx="1"/>
    <rect x="10" y="23" width="45" height="3" rx="1"/>
    <rect x="55" y="13" width="22" height="8" rx="1" opacity="0.8"/>
    <rect x="77" y="15" width="30" height="4" rx="0.5"/>
    <polygon points="8,17 2,20 8,23" opacity="0.9"/>
    <rect x="14" y="23" width="8" height="10" rx="1" opacity="0.9"/>
    <rect x="18" y="14" width="3" height="4" rx="0.5" opacity="0.7"/>
    <rect x="55" y="11" width="4" height="2" rx="0.5" opacity="0.6"/>
    <rect x="75" y="12" width="2" height="3" rx="0.5" opacity="0.6"/>
    <rect x="25" y="23" width="6" height="8" rx="1" opacity="0.7"/>
  </svg>
);

const PongIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 80 50" className={className} fill="currentColor">
    <rect x="4" y="16" width="5" height="18" rx="2" opacity="0.9"/>
    <rect x="71" y="16" width="5" height="18" rx="2" opacity="0.9"/>
    <circle cx="40" cy="25" r="4"/>
    <line x1="40" y1="2" x2="40" y2="48" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4,4" opacity="0.4"/>
  </svg>
);

const FPS_WAGER_AMOUNTS = [1, 5, 10, 25, 50, 100];
const TYPING_LINES = [
  "SELECT YOUR STAKE.",
  "WINNER TAKES ALL.",
  "NO MERCY. PURE SKILL.",
  "CRYPTO REWARDS AWAIT.",
];

function FPSWagerModal({ onClose, onPlay }: { onClose: () => void; onPlay: (amount: number) => void }) {
  const [selected, setSelected] = useState<number | null>(null);
  const [typedText, setTypedText] = useState("");
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [muzzleFlash, setMuzzleFlash] = useState(false);
  const intervalRef = useRef<any>(null);

  useEffect(() => {
    const line = TYPING_LINES[lineIdx];
    intervalRef.current = setTimeout(() => {
      if (!deleting) {
        if (charIdx < line.length) {
          setTypedText(line.substring(0, charIdx + 1));
          setCharIdx(c => c + 1);
        } else {
          setTimeout(() => setDeleting(true), 1200);
        }
      } else {
        if (charIdx > 0) {
          setTypedText(line.substring(0, charIdx - 1));
          setCharIdx(c => c - 1);
        } else {
          setDeleting(false);
          setLineIdx(i => (i + 1) % TYPING_LINES.length);
        }
      }
    }, deleting ? 35 : 70);
    return () => clearTimeout(intervalRef.current);
  }, [charIdx, deleting, lineIdx]);

  useEffect(() => {
    const flash = setInterval(() => {
      setMuzzleFlash(true);
      setTimeout(() => setMuzzleFlash(false), 80);
    }, 3200);
    return () => clearInterval(flash);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="relative w-full max-w-4xl bg-[#111111] border border-white/10 rounded-2xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
        {/* Orange top bar */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#FF6B00] via-[#F54D12] to-transparent" />

        {/* LEFT — wager selection */}
        <div className="w-full md:w-[45%] p-8 flex flex-col justify-between border-b md:border-b-0 md:border-r border-white/5">
          <div>
            {/* Header with AK icon and typewriter */}
            <div className="flex items-center gap-3 mb-2">
              <div className={`relative transition-all duration-75 ${muzzleFlash ? 'brightness-200' : ''}`}>
                <AK47Icon className="h-8 w-auto text-[#FF6B00]" />
                {muzzleFlash && (
                  <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-yellow-300 rounded-full blur-sm opacity-90 pointer-events-none" />
                )}
              </div>
            </div>
            <div className="h-8 mb-1">
              <span className="text-[#FF6B00] font-mono font-bold text-lg tracking-widest uppercase">
                {typedText}
                <span className="animate-pulse">|</span>
              </span>
            </div>
            <h2 className="text-3xl font-extrabold text-white tracking-tight mb-1">FPS ARENA</h2>
            <p className="text-white/40 text-sm mb-8 leading-relaxed">
              Buy weapons each round. First to <span className="text-white/70 font-bold">7 wins</span> takes the pot.
              10% platform fee on payout.
            </p>

            {/* Wager grid */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {FPS_WAGER_AMOUNTS.map(amount => (
                <button
                  key={amount}
                  onClick={() => setSelected(amount)}
                  className={`relative py-4 rounded-xl border transition-all flex flex-col items-center gap-1 ${
                    selected === amount
                      ? "border-[#FF6B00] bg-[#FF6B00]/15 shadow-[0_0_20px_rgba(255,107,0,0.25)]"
                      : "border-white/10 bg-white/3 hover:border-white/25 hover:bg-white/5"
                  }`}
                >
                  {selected === amount && (
                    <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />
                  )}
                  <span className={`text-2xl font-black ${selected === amount ? 'text-[#FF6B00]' : 'text-white/70'}`}>
                    ${amount}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white/30">
                    {amount <= 1 ? 'Lite' : amount <= 5 ? 'Starter' : amount <= 10 ? 'Pro' : amount <= 25 ? 'Expert' : amount <= 50 ? 'Elite' : 'Whale'}
                  </span>
                </button>
              ))}
            </div>

            {selected && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-6 text-xs text-white/50 flex justify-between">
                <span>Total pot</span>
                <span className="text-white font-bold">${selected * 2} USDT</span>
                <span>You win</span>
                <span className="text-[#FF6B00] font-bold">${(selected * 2 * 0.9).toFixed(2)}</span>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-white/10 text-white/50 hover:text-white hover:border-white/30 font-semibold text-sm transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => selected && onPlay(selected)}
              disabled={!selected}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#FF6B00] to-[#F54D12] text-white font-bold text-sm tracking-wide disabled:opacity-30 disabled:cursor-not-allowed hover:shadow-[0_0_25px_rgba(255,107,0,0.4)] hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
            >
              <Target className="w-4 h-4" />
              Enter Match
            </button>
          </div>
        </div>

        {/* RIGHT — preview panel */}
        <div className="w-full md:w-[55%] relative bg-[#0D0D0D] flex flex-col items-center justify-center overflow-hidden min-h-[340px]">
          {/* Dust2-style background grid */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `linear-gradient(rgba(255,107,0,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,0,0.3) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }} />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0D0D0D]" />
          <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-[#0D0D0D] to-transparent" />

          {/* Central AK scene */}
          <div className="relative z-10 flex flex-col items-center gap-6 p-8">
            {/* Big AK icon with glow */}
            <div className={`relative transition-all duration-75 ${muzzleFlash ? 'drop-shadow-[0_0_20px_rgba(255,220,0,0.9)]' : 'drop-shadow-[0_0_8px_rgba(255,107,0,0.4)]'}`}>
              <AK47Icon className="h-20 w-auto text-white" />
              {muzzleFlash && (
                <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-yellow-300 rounded-full blur-lg opacity-80" />
              )}
            </div>

            {/* Weapon stats */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
              {[
                { label: "Damage", value: "35", icon: "💥" },
                { label: "Fire Rate", value: "600rpm", icon: "⚡" },
                { label: "Mag Size", value: "30", icon: "🔋" },
                { label: "Type", value: "Full Auto", icon: "🎯" },
              ].map(stat => (
                <div key={stat.label} className="bg-white/5 border border-white/8 rounded-lg px-3 py-2 flex items-center gap-2">
                  <span className="text-base">{stat.icon}</span>
                  <div>
                    <div className="text-[10px] text-white/40 uppercase tracking-wider font-bold">{stat.label}</div>
                    <div className="text-sm text-white font-bold">{stat.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Available weapons strip */}
            <div className="flex items-center gap-2 w-full justify-center">
              {["KNIFE", "G-18", "AK-47", "M4A4", "AWP"].map((name, i) => (
                <div key={name} className={`px-2 py-1 rounded text-[10px] font-bold tracking-wider border ${i === 2 ? 'bg-[#FF6B00]/20 border-[#FF6B00]/50 text-[#FF6B00]' : 'bg-white/5 border-white/10 text-white/40'}`}>
                  {name}
                </div>
              ))}
            </div>

            <p className="text-white/30 text-xs text-center max-w-xs leading-relaxed">
              Tactical 1v1 — buy phase each round. Outshoot your opponent to claim the crypto pot.
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ onPlayGame, onOpenTournaments }: DashboardProps) {
  const [showFPSModal, setShowFPSModal] = useState(false);

  return (
    <main className="flex-grow flex flex-col w-full h-full bg-[#0A0A0A]">

      {/* ── Hero ── */}
      <Hero
        onLaunch={() => {
          const games = document.getElementById("games-grid");
          if (games) games.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />

      {/* ── Live Ticker ── */}
      <LiveTicker />

      {/* ── Games grid ── */}
      <GamesGrid
        onPlayPong={() => onPlayGame('pong')}
        onOpenFPSModal={() => onPlayGame('fps')}
        onPlayTowerDefense={() => onPlayGame('tower-defense')}
        onPlaySnake={() => onPlayGame('snake')}
        onPlayMath={() => onPlayGame('math')}
        onOpenTournaments={onOpenTournaments}
      />

      {/* ── How It Works ── */}
      <HowItWorks />

      {/* ── Provably Fair ── */}
      <ProvablyFair />

      {/* ── Leaderboard ── */}
      <Leaderboard />

      {/* ── FAQ ── */}
      <FAQ />

      {showFPSModal && (
        <FPSWagerModal
          onClose={() => setShowFPSModal(false)}
          onPlay={(amount) => {
            setShowFPSModal(false);
            onPlayGame('fps', amount);
          }}
        />
      )}
    </main>
  );
}
